import { useEffect, useMemo, useState } from "react"
import CodeMirror from "@uiw/react-codemirror"
import { json } from "@codemirror/lang-json"
import { linter, lintGutter } from "@codemirror/lint"
import { EditorView } from "@codemirror/view"
import JSON5 from "json5"
import { cn } from "@/lib/cn"

// Drop-in replacement for <Textarea> when the value is a JSON string.
// Adds:
//   - Syntax highlighting (CodeMirror 6 + lang-json).
//   - Inline lint errors with line/col underline + gutter dot.
//   - "Fix" pill that appears when JSON.parse fails BUT JSON5.parse
//     succeeds — i.e. the buffer has unquoted keys, single quotes,
//     trailing commas, or comments. One click reformats the buffer to
//     strict JSON via JSON5.parse → JSON.stringify.
//
// API mirrors a controlled <textarea>:
//   value      — the JSON string
//   onChange   — called with the new text
//   minHeight  — CSS height for the editor body (default 220px)
//   disabled   — read-only mode
//
// The component does NOT silently rewrite the buffer — every change
// flows through onChange so the parent owns the canonical text. Auto-fix
// is opt-in (operator clicks "Fix") to avoid surprising edits.
type Props = {
  value: string
  onChange: (next: string) => void
  minHeight?: string
  disabled?: boolean
  placeholder?: string
}

type LintState =
  | { kind: "ok" }
  | { kind: "json5_fixable"; line: number; col: number; message: string; fixed: string }
  | { kind: "broken"; line: number; col: number; message: string }

function classifyError(message: string): string {
  // Common JSON.parse messages → friendlier hints.
  if (/Unexpected token .+? in JSON/i.test(message)) {
    if (/}/.test(message)) return "Unexpected `}` — extra closing brace, or trailing comma before it"
    if (/]/.test(message)) return "Unexpected `]` — extra closing bracket, or trailing comma before it"
    return message + " — likely an unquoted key, single quotes, or a trailing comma"
  }
  if (/Unexpected end of JSON input/i.test(message)) return "Buffer ends mid-value — missing closing brace or bracket"
  if (/Unexpected string in JSON/i.test(message)) return message + " — usually a missing comma between values"
  return message
}

function parseTextOffset(text: string, offset: number): { line: number; col: number } {
  if (!offset || offset < 0) return { line: 1, col: 1 }
  const before = text.slice(0, offset)
  const lines = before.split("\n")
  return { line: lines.length, col: lines[lines.length - 1].length + 1 }
}

function lintBuffer(text: string): LintState {
  if (text.trim().length === 0) return { kind: "ok" }
  try {
    JSON.parse(text)
    return { kind: "ok" }
  } catch (e) {
    const strictMsg = e instanceof Error ? e.message : String(e)
    // Try JSON5: if it parses, the buffer is "fixable" (unquoted keys,
    // single quotes, trailing commas, comments). Re-emit as strict JSON.
    try {
      const value = JSON5.parse(text)
      const fixed = JSON.stringify(value, null, 2)
      // Locate the strict-JSON.parse error position (best-effort).
      const m = strictMsg.match(/at position (\d+)/)
      const offset = m ? parseInt(m[1], 10) : 0
      const { line, col } = parseTextOffset(text, offset)
      return {
        kind: "json5_fixable",
        line,
        col,
        message: classifyError(strictMsg),
        fixed,
      }
    } catch {
      const m = strictMsg.match(/at position (\d+)/)
      const offset = m ? parseInt(m[1], 10) : 0
      const { line, col } = parseTextOffset(text, offset)
      return {
        kind: "broken",
        line,
        col,
        message: classifyError(strictMsg),
      }
    }
  }
}

export function JsonEditor({
  value,
  onChange,
  minHeight = "220px",
  disabled = false,
  placeholder,
}: Props) {
  const [lint, setLint] = useState<LintState>(() => lintBuffer(value))

  useEffect(() => {
    setLint(lintBuffer(value))
  }, [value])

  const cmExtensions = useMemo(
    () => [
      json(),
      lintGutter(),
      // CodeMirror linter — surfaces JSON.parse errors inline as red
      // underlines and a gutter marker. Returns at most one diagnostic
      // since JSON.parse stops at the first error.
      linter((view) => {
        const text = view.state.doc.toString()
        if (text.trim().length === 0) return []
        try {
          JSON.parse(text)
          return []
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e)
          const m = msg.match(/at position (\d+)/)
          const from = m ? Math.max(0, parseInt(m[1], 10) - 1) : 0
          const to   = Math.min(text.length, from + 1)
          return [{ from, to, severity: "error", message: classifyError(msg) }]
        }
      }),
      EditorView.lineWrapping,
    ],
    [],
  )

  function applyFix() {
    if (lint.kind === "json5_fixable") onChange(lint.fixed)
  }

  return (
    <div
      className={cn(
        "rounded-control border bg-white/5 transition focus-within:border-brand-red/50",
        lint.kind === "broken" ? "border-status-error/60" : "border-border",
      )}
    >
      <CodeMirror
        value={value}
        onChange={(v) => onChange(v)}
        extensions={cmExtensions}
        editable={!disabled}
        readOnly={disabled}
        placeholder={placeholder}
        minHeight={minHeight}
        basicSetup={{
          lineNumbers: true,
          foldGutter: true,
          highlightActiveLine: !disabled,
          autocompletion: false,
        }}
        theme="dark"
        className="overflow-hidden rounded-control text-xs [&_.cm-editor]:bg-transparent [&_.cm-gutters]:border-r [&_.cm-gutters]:border-border [&_.cm-gutters]:bg-app-bg/40"
      />
      <StatusBar lint={lint} onFix={applyFix} />
    </div>
  )
}

function StatusBar({ lint, onFix }: { lint: LintState; onFix: () => void }) {
  if (lint.kind === "ok") {
    return (
      <div className="flex items-center justify-between border-t border-border px-3 py-1.5 text-[11px] text-app-muted">
        <span>Valid JSON</span>
      </div>
    )
  }

  if (lint.kind === "json5_fixable") {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-status-warning/30 bg-status-warning/10 px-3 py-1.5 text-[11px] text-status-warning">
        <span className="font-medium">Line {lint.line}:{lint.col}</span>
        <span className="flex-1">{lint.message}</span>
        <button
          type="button"
          onClick={onFix}
          className="rounded-control border border-status-warning/40 bg-status-warning/20 px-2 py-0.5 font-semibold text-status-warning transition hover:bg-status-warning/30"
        >
          Auto-fix
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-status-error/30 bg-status-error/10 px-3 py-1.5 text-[11px] text-status-error">
      <span className="font-medium">Line {lint.line}:{lint.col}</span>
      <span className="flex-1">{lint.message}</span>
    </div>
  )
}
